import React, { useState, useEffect } from "react";
import { Form, Select, message, Button } from "antd";
import {
    SwapOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import { Spinner } from '@operations/shared';
import helper from "../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import { changePendingUserCompanyApi } from "../../utils/apisConsts";
import { request } from '../../../../shared/src/apis';
import { stepperMoving } from "../../stores/actions";


const { Option } = Select;

// Interfaces following project conventions

interface ICompany {
    id: number;
    name: string;
    arabicName: string;
    companyType: string;
    deployed: boolean;
}

interface IChangeCompanyForm {
    newCompanyID: number;
    reason: string;
}

export const ChangeCompany = () => {
    const [form] = Form.useForm<IChangeCompanyForm>();

    const [loading, setLoading] = useState(false);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [companies, setCompanies] = useState<ICompany[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
    const userInfo = useSelector((state: any) => state.actionsReducer.info);
    const dispatch = useDispatch();

    useEffect(() => {
        fetchCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCompanies = async () => {
        try {
            setCompaniesLoading(true);
            const response: any = await helper.getCompanies();

            if (response?.data?.status === 200 && response?.data?.data) {
                const companiesData = response?.data?.data
                    .filter((company: any) => company?.id !== userInfo?.userID)
                    .map((company: any) => ({
                        id: company?.id,
                        name: company?.name || company?.nameEn || '',
                        arabicName: company?.arabicName || company?.nameAr || '',
                        companyType: company?.companyType || company?.type || 'N/A',
                        deployed: company?.deployed || company?.isDeployed || true,
                    }));

                setCompanies(companiesData);
            } else {
                message.error("Failed to fetch companies data");
                console.error("Invalid API response:", response);
            }
        } catch (error) {
            message.error("Failed to fetch companies");
            console.error("Error fetching companies:", error);
        } finally {
            setCompaniesLoading(false);
        }
    };

    const handleCompanyChange = (companyId: number) => {
        const company = companies?.find((c) => c?.id === companyId);
        setSelectedCompany(company || null);
    };

   
    const submitUserToNewCompany = async () => {
        if (!selectedCompany) {
            message.error("Please select a company first");
            return;
        }

        try {
            setLoading(true);
            const data = {
                userID: userInfo?.userID,
                companyID: selectedCompany?.id
            }

            const apiData: any = await dispatch(request({ method: 'PATCH', url: changePendingUserCompanyApi, data: data }))
            const response = await apiData.data;

            if (response?.status === 200) {
                message.success("User successfully moved to new company");
                form.resetFields();
                setSelectedCompany(null);
                dispatch(stepperMoving(0))
            } else {
                message.error(response?.message || "Failed to change user company");
            }
        } catch (error: any) {
            console.error("Error changing user company:", error);
            message.error(error?.response?.message || "An error occurred while changing user company");
        } finally {
            setLoading(false);
        }
    };
    return (
        <div
            className="changeCompany"
            style={{ padding: "20px", maxWidth: "700px", margin: "0 auto" }}
        >
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
                <SwapOutlined
                    style={{ fontSize: "28px", color: "#1890ff", marginBottom: "12px" }}
                />
                <h2 style={{ margin: "0", color: "#333" }}>Change User Company</h2>
                <p style={{ margin: "8px 0 0 0", color: "#666", fontSize: "14px" }}>
                    Transfer user to a different company in the system
                </p>
            </div>

            <div
                style={{
                    background: "linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%)",
                    padding: "20px",
                    borderRadius: "12px",
                    marginBottom: "25px",
                    border: "1px solid #d6e4ff",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "15px",
                    }}
                >
                    <div>
                        <div
                            style={{ fontWeight: "bold", fontSize: "18px", color: "#1890ff" }}
                        >
                            {userInfo?.name}
                        </div>
                        <div style={{ color: "#666", fontSize: "14px", marginTop: "4px" }}>
                            Staff ID: {userInfo?.staff_id}
                        </div>
                        <div style={{ color: "#666", fontSize: "14px" }}>
                            User ID: {userInfo?.userID}
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div
                            style={{ fontWeight: "bold", color: "#1890ff", fontSize: "16px" }}
                        >
                            {userInfo?.companyName}
                        </div>
                        <div
                            style={{
                                color: "#e3c420",
                                fontSize: "14px",
                                marginTop: "4px",
                                fontWeight: 600,
                            }}
                        >
                            ● {userInfo?.userStatus}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "15px",
                        padding: "15px",
                        background: "white",
                        borderRadius: "8px",
                        border: "1px solid #e8e8e8",
                    }}
                >
                    <div style={{ textAlign: "center" }}>
                        <div
                            style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
                        >
                            Job Title
                        </div>
                        <div style={{ fontWeight: "bold", color: "#333" }}>
                            {userInfo?.jobTitle}
                        </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <div
                            style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
                        >
                            Department
                        </div>
                        <div style={{ fontWeight: "bold", color: "#333" }}>
                            {userInfo.department?.nameEn ?? 'No Department'}
                        </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <div
                            style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
                        >
                            Hiring Date
                        </div>
                        <div style={{ fontWeight: "bold", color: "#333" }}>
                            {userInfo?.hiringDate
                                ? new Date(userInfo?.hiringDate).toLocaleDateString()
                                : "N/A"}
                        </div>
                    </div>
                </div>
            </div>

            <div
                style={{
                    background: "white",
                    padding: "20px",
                    borderRadius: "12px",
                    marginBottom: "25px",
                    border: "1px solid #e8e8e8",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <h3 style={{ margin: "0 0 15px 0", color: "#333", fontSize: "16px" }}>
                    <SwapOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                    Select New Company
                </h3>

                <div style={{ marginBottom: "20px" }}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "bold",
                            color: "#333",
                        }}
                    >
                        New Company <span>*</span>
                    </label>
                    <Select
                        placeholder="Choose company from the list"
                        loading={companiesLoading}
                        onChange={handleCompanyChange}
                        showSearch
                        style={{ width: "100%" }}
                        size="large"
                        filterOption={(input, option) => {
                            if (!option || !option?.children) return false;
                            const childrenText = Array.isArray(option?.children)
                                ? option?.children?.join(" ")
                                : String(option?.children);
                            return childrenText.toLowerCase().includes(input.toLowerCase());
                        }}
                    >
                        {companies?.map((company) => (
                            <Option key={company?.id} value={company?.id}>
                                {company?.name} - {company?.companyType}
                            </Option>
                        ))}
                    </Select>
                    {companiesLoading && (
                        <div
                            style={{ marginTop: "8px", color: "#1890ff", fontSize: "12px" }}
                        >
                            <Spinner size="small" />
                        </div>
                    )}
                </div>

                {selectedCompany && (
                    <div
                        style={{
                            background: "#f6ffed",
                            padding: "15px",
                            borderRadius: "8px",
                            border: "1px solid #b7eb8f",
                            marginBottom: "20px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "8px",
                            }}
                        >
                            <InfoCircleOutlined
                                style={{ color: "#52c41a", marginRight: "8px" }}
                            />
                            <span style={{ fontWeight: "bold", color: "#52c41a" }}>
                                Selected Company
                            </span>
                        </div>
                        <div style={{ color: "#333" }}>
                            <strong>{selectedCompany?.name}</strong> (
                            {selectedCompany?.companyType})
                        </div>
                        <div style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                            Company ID: {selectedCompany?.id}
                        </div>
                    </div>
                )}
            </div>

            <div style={{ textAlign: "center" }}>
                <Button
                    loading={loading}
                    disabled={loading || !selectedCompany}
                    onClick={submitUserToNewCompany}
                    type="primary"
                    style={{
                        height: "40px",
                        padding: "0 24px",
                        borderRadius: "8px",
                        background: loading || !selectedCompany ? "#d9d9d9" : "#1890ff",
                        border: "none",
                        color: "white",
                        fontWeight: 500,
                        fontSize: "14px",
                        boxShadow: loading || !selectedCompany
                            ? "none"
                            : "0 2px 4px rgba(24,144,255,0.3)",
                    }}
                    onMouseOver={(e) => {
                        if (!loading && selectedCompany) {
                            e.currentTarget.style.background = "#40a9ff";
                            e.currentTarget.style.transform = "translateY(-1px)";
                        }
                    }}
                    onMouseOut={(e) => {
                        if (!loading && selectedCompany) {
                            e.currentTarget.style.background = "#1890ff";
                            e.currentTarget.style.transform = "translateY(0)";
                        }
                    }}
                >
                    <span>
                        <SwapOutlined style={{ marginRight: "8px" }} />
                        Move User to New Company
                    </span>
                </Button>
            </div>

            {loading && (
                <div
                    style={{
                        textAlign: "center",
                        marginTop: "20px",
                        padding: "15px",
                        background: "#fff7e6",
                        borderRadius: "8px",
                        border: "1px solid #ffd591",
                    }}
                >
                    <Spinner size="large" />
                    <div style={{ marginTop: "10px", color: "#d46b08", fontWeight: 500 }}>
                        Processing company change request...
                    </div>
                    <div style={{ color: "#666", fontSize: "12px", marginTop: "5px" }}>
                        Please wait while we transfer the user
                    </div>
                </div>
            )}
        </div>
    );
};
